import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Form, Input, Modal, Typography, notification } from "antd";
import {
  IMPORT_CNF_BILL_DELETE_ENDPOINT,
  IMPORT_CNF_BILL_DISBURSE_ENDPOINT,
  IMPORT_CNF_BILL_LIST_ENDPOINT,
  IMPORT_CNF_BILL_REQUESTED_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  EXTERNAL_CNF_BILLS_CREATE_PERMISSION,
  EXTERNAL_CNF_BILLS_DELETE_PERMISSION,
  EXTERNAL_CNF_BILLS_EDIT_PERMISSION,
  EXTERNAL_CNF_BILLS_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";
import { checkPermission } from "./config/checkPermission";
import { ROLES } from "./config/constants/roles";

const formatDate = (value) =>
  value && moment(value).isValid() ? moment(value).format("DD-MMM-YYYY") : "—";

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getTotalBillAmount = (bill) =>
  (bill.bill_details || []).reduce((sum, d) => sum + (Number(d.amount) || 0), 0) +
  (Number(bill.agency_commission) || 0);

export default function ImportCnfBillList() {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location?.pathname;
  const loggedInUserId = parseInt(localStorage.getItem("user_id"), 10);
  const loggedInUserRole = localStorage.getItem("user_type");

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [canRequest, setCanRequest] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [canReject, setCanReject] = useState(false);
  const [disburseModalVisible, setDisburseModalVisible] = useState(false);
  const [disburseRecord, setDisburseRecord] = useState(null);
  const [disburseForm] = Form.useForm();

  const { Title } = Typography;
  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsed = filterStatus ? JSON.parse(filterStatus) : {};
    localStorage.setItem(
      "filter_status",
      JSON.stringify({
        ...parsed,
        [routerPath]: showFilter,
      }),
    );
  }, [showFilter, routerPath]);

  const fetchList = useCallback(
    async (filterValues) => {
      setLoading(true);
      setFetchError(null);
      const query = `${IMPORT_CNF_BILL_LIST_ENDPOINT}?page=${currentPage}&per_page=${pageSize}`;
      const bodyData = {
        ...(filterValues?.name && { invoice_number: filterValues.name }),
        ...(filterValues?.status && { status: filterValues.status }),
      };
      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        setTableData(
          data.map((item, index) => ({
            ...item,
            key: item.id || `im-cnf-${index}`,
          })),
        );
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultPageSize: response.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
      } else {
        setFetchError("Could not load this list.");
      }
      setLoading(false);
    },
    [currentPage, pageSize],
  );

  useEffect(() => {
    fetchList(filterData);
  }, [refresh, filterData, fetchList]);

  const refetch = useCallback(() => setRefresh((p) => p + 1), []);

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);

    const allSameStatus =
      newSelectedRows.length > 0 &&
      newSelectedRows.every((row) => row.status === newSelectedRows[0].status);

    if (allSameStatus) {
      setCanRequest(
        newSelectedRows.some((row) =>
          checkPermission(
            loggedInUserRole,
            row.status,
            loggedInUserId === row?.user?.id,
            "request",
            row?.department_approve,
            row?.director_approve,
          ),
        ),
      );
      setCanApprove(
        newSelectedRows.some((row) =>
          checkPermission(
            loggedInUserRole,
            row.status,
            loggedInUserId === row?.user?.id,
            "approve",
            row?.department_approve,
            row?.director_approve,
          ),
        ),
      );
      setCanReject(
        newSelectedRows.some((row) =>
          checkPermission(
            loggedInUserRole,
            row.status,
            loggedInUserId === row?.user?.id,
            "reject",
            row?.department_approve,
            row?.director_approve,
          ),
        ),
      );
    } else {
      setCanRequest(false);
      setCanApprove(false);
      setCanReject(false);
    }
  };

  const handleWorkflowAction = async (status) => {
    if (!selectedRows.length) {
      notification.warning({
        message: "Warning",
        description: "No rows selected",
        placement: "topRight",
      });
      return;
    }

    const isResubmit = status === "requested";
    Modal.confirm({
      title: status === "approved"
        ? "Confirm approval"
        : isResubmit
          ? "Confirm re-submission"
          : "Confirm rejection",
      content: status === "approved"
        ? "Approve the selected bill request(s)?"
        : isResubmit
          ? "Re-submit the selected bill request(s) for approval?"
          : "Reject the selected bill request(s)?",
      okText: "Yes",
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);
        const formData = new FormData();
        formData.append("requested_id", JSON.stringify(selectedRows.map((r) => r.id)));
        formData.append("status", status);

        const response = await postData(IMPORT_CNF_BILL_REQUESTED_ENDPOINT, formData);
        if (response && (response?.code === 200 || response?.status === 200)) {
          alertPop("success", response.message || "Submitted successfully");
          setSelectedRowKeys([]);
          setSelectedRows([]);
          refetch();
        }
        setLoading(false);
      },
    });
  };

  const handleDisburse = async () => {
    try {
      const values = await disburseForm.validateFields();
      setLoading(true);
      const response = await postData(IMPORT_CNF_BILL_DISBURSE_ENDPOINT, {
        bill_id: disburseRecord.id,
        receive_by: values.receive_by,
      });
      if (response && (response?.code === 200 || response?.status === 200)) {
        notification.success({
          message: "Success",
          description: response?.message || "Disbursed successfully",
          placement: "topRight",
        });
        setDisburseModalVisible(false);
        setDisburseRecord(null);
        disburseForm.resetFields();
        refetch();
      }
    } catch (e) {
      // validation
    } finally {
      setLoading(false);
    }
  };

  const onDelete = useCallback(
    (id) => {
      Modal.confirm({
        title: "Confirm deletion",
        content: "Delete this Import C&F bill?",
        okType: "danger",
        onOk: async () => {
          const res = await deleteData(`${IMPORT_CNF_BILL_DELETE_ENDPOINT}/${id}`);
          if (res && (res?.code === 200 || res?.status === 200)) {
            refetch();
          }
        },
      });
    },
    [refetch],
  );

  const columns = useMemo(
    () => [
      {
        title: "Invoice Number",
        key: "inv",
        render: (_, r) =>
          r.import_invoice?.invoice_number || r.invoice_number || "—",
      },
      {
        title: "LC Number",
        key: "lc",
        render: (_, r) => r.import_invoice?.b2blc_info?.b2b_lc_number || "—",
      },
      {
        title: "Invoice Quantity",
        key: "qty",
        align: "right",
        render: (_, r) => r.import_invoice?.quantity_ctn ?? "—",
      },
      {
        title: "Quantity Unit",
        key: "qty_unit",
        render: (_, r) => r.import_invoice?.import_quantity_unit || "—",
      },
      {
        title: "Invoice Value",
        key: "invoice_value",
        align: "right",
        render: (_, r) =>
          r.import_invoice?.invoice_value != null
            ? formatAmount(r.import_invoice.invoice_value)
            : "—",
      },
      {
        title: "Total Bill Amount",
        key: "total_bill_amount",
        align: "right",
        render: (_, r) => formatAmount(getTotalBillAmount(r)),
      },
      {
        title: "Bill of Entry Date",
        dataIndex: "bill_of_entry_date",
        render: (v) => formatDate(v),
      },
      {
        title: "Bill Create Date",
        dataIndex: "created_at",
        render: (v) => formatDate(v),
      },
      {
        title: "Bill Number",
        dataIndex: "bill_no",
        render: (v) => v || "—",
      },
      {
        title: "Requested By",
        key: "requested_by",
        render: (_, r) => r.user?.name || "—",
      },
      // {
      //   title: "Status",
      //   dataIndex: "status",
      //   render: (status) => <ImportCnfBillStatusTag status={status} />,
      // },
      {
        title: "Action",
        key: "action",
        width: 120,
        render: (_, record) => {
          const isCreator = loggedInUserId === record?.user?.id;
          const canView = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "view",
            record?.department_approve,
            record?.director_approve,
          );
          const canUpdate = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "update",
            record?.department_approve,
            record?.director_approve,
          );
          const canDisburse = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "disburse",
            record?.department_approve,
            record?.director_approve,
          );

          return (
            <Dropdown
              menu={{
                items: [
                  canView &&
                    hasPermission([EXTERNAL_CNF_BILLS_VIEW_PERMISSION]) && {
                      key: "view",
                      label: "View",
                      icon: <EyeOutlined />,
                      onClick: () =>
                        navigate(
                          `/import-export-expense-tracking/external-cnf-bills/import-cnf-bill/view/${record.id}`,
                        ),
                    },
                  canUpdate &&
                    hasPermission([EXTERNAL_CNF_BILLS_EDIT_PERMISSION]) && {
                      key: "edit",
                      label: "Edit",
                      icon: <EditOutlined />,
                      onClick: () =>
                        navigate(
                          `/import-export-expense-tracking/external-cnf-bills/import-cnf-bill/edit/${record.id}`,
                        ),
                    },
                  canDisburse && {
                    key: "disburse",
                    label: "Disburse",
                    icon: <CheckOutlined />,
                    onClick: () => {
                      setDisburseRecord(record);
                      setDisburseModalVisible(true);
                    },
                  },
                  isCreator &&
                    (record.status === "created" || record.status === "requested") &&
                    hasPermission([EXTERNAL_CNF_BILLS_DELETE_PERMISSION]) && {
                      key: "del",
                      label: "Delete",
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => onDelete(record.id),
                    },
                ].filter(Boolean),
              }}
            >
              <Button type="link" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [loggedInUserId, loggedInUserRole, navigate, onDelete],
  );

  const handleTableChange = (pagination) => {
    if (pagination.current !== currentPage) setCurrentPage(pagination.current);
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setCurrentPage(1);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const showWorkflowButtons =
    loggedInUserRole === ROLES.DEPARTMENT_HEAD ||
    loggedInUserRole === ROLES.DIRECTOR ||
    loggedInUserRole === ROLES.ACCOUNTS ||
    canRequest;

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <div style={{ display: "flex", alignItems: "center" }}>
              {showWorkflowButtons && selectedRowKeys.length > 0 && canReject && (
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleWorkflowAction("rejected")}
                  style={{ marginRight: 10 }}
                >
                  Reject
                </Button>
              )}
              {showWorkflowButtons && selectedRowKeys.length > 0 && canRequest && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => handleWorkflowAction("requested")}
                  style={{ marginRight: 10 }}
                >
                  Re-submit
                </Button>
              )}
              {showWorkflowButtons && selectedRowKeys.length > 0 && canApprove && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => handleWorkflowAction("approved")}
                  style={{ marginRight: 10 }}
                >
                  Approve
                </Button>
              )}
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={() =>
                  navigate(
                    "/import-export-expense-tracking/external-cnf-bills/import-cnf-bill/add",
                  )
                }
                disabled={!hasPermission([EXTERNAL_CNF_BILLS_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([EXTERNAL_CNF_BILLS_CREATE_PERMISSION])
                    ? "Add New"
                    : "No permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            </div>
          }
          title={<Title level={5}>Import C&F Bill</Title>}
        />
      }
      content={
        <>
          <FilterComponent
            tableData={tableData}
            filterAPI={fetchList}
            showFilter={showFilter}
            setShowFilter={setShowFilter}
          />
          <div className="table" style={{ marginTop: 10 }}>
            <ListTable
              loading={loading}
              error={fetchError}
              onRetry={() => fetchList(filterData)}
              dataSource={tableData}
              columns={columns}
              rowKey="id"
              rowSelection={showWorkflowButtons ? rowSelection : undefined}
              pagination={{
                current: currentPage,
                pageSize,
                total: metaData?.total || 0,
                showSizeChanger: true,
                showTotal: (t) => `Total ${t} items`,
              }}
              onChange={handleTableChange}
              bordered
              scroll={{ x: "max-content" }}
            />
          </div>
          <Modal
            title="Disburse Import C&F Bill"
            open={disburseModalVisible}
            onCancel={() => {
              setDisburseModalVisible(false);
              setDisburseRecord(null);
              disburseForm.resetFields();
            }}
            onOk={handleDisburse}
            okText="Confirm Disbursement"
          >
            <p>
              Invoice:{" "}
              {disburseRecord?.import_invoice?.invoice_number ||
                disburseRecord?.invoice_number ||
                "—"}
            </p>
            <Form form={disburseForm} layout="vertical">
              <Form.Item
                name="receive_by"
                label="Received By"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="Name of recipient" />
              </Form.Item>
            </Form>
          </Modal>
        </>
      }
    />
  );
}
